import { useEffect, useRef, useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Box, IconButton, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';

const Videos = () => {
  // List of all videos in the public/assets/videos folder
  const videoList = [
    '/assets/videos/1000005112.mp4',
    '/assets/videos/1000005123.mp4',
    '/assets/videos/17845356702544682.mp4',
    '/assets/videos/17862607314500423.mp4',
    '/assets/videos/17918309697112159.mp4',
    '/assets/videos/17975918546970898.mp4',
    '/assets/videos/18029103545772992.mp4',
    '/assets/videos/18070533413483463.mp4',
    '/assets/videos/18103978120582559.mp4',
    '/assets/videos/18114706822615312.mp4',
    '/assets/videos/18139892764422430.mp4',
    '/assets/videos/18338537947162154.mp4',
    '/assets/videos/18413845438191464.mp4',
  ];

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle video end - move to next video
  const handleVideoEnd = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videoList.length);
  };

  // Start playing (for initial play button)
  const startPlaying = () => {
    if (videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
        setShowPlayButton(false);
      }).catch((error) => {
        console.error('Play error:', error);
      });
    }
  };

  // Play/Pause toggle
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Next video
  const nextVideo = () => {
    setCurrentVideoIndex((prevIndex) => (prevIndex + 1) % videoList.length);
  };

  // Previous video
  const previousVideo = () => {
    setCurrentVideoIndex((prevIndex) => 
      prevIndex === 0 ? videoList.length - 1 : prevIndex - 1
    );
  };

  // Fullscreen toggle
  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!isFullscreen) {
        // Enter fullscreen
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if ((containerRef.current as any).webkitRequestFullscreen) {
          // Safari/iOS support
          await (containerRef.current as any).webkitRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          // Safari/iOS support
          await (document as any).webkitExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen toggle error:', error);
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(document.fullscreenElement || (document as any).webkitFullscreenElement)
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Auto-play when video changes
  useEffect(() => {
    if (videoRef.current && isPlaying) {
      videoRef.current.play().catch((error) => {
        console.error('Auto-play error:', error);
        setIsPlaying(false);
      });
    }
  }, [currentVideoIndex, isPlaying]);

  // Toggle controls visibility (for touch devices)
  const handleVideoClick = () => {
    setShowControls(true);
    
    // Auto-hide controls after 3 seconds
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
    <Helmet>
      <title>Tattoo Removal Videos | Atomic Tattoo Removal</title>
      <meta name="description" content="Watch real laser tattoo removal sessions and results at Atomic Tattoo Removal in Wrentham, MA. See our process, then book a free consultation." />
      <meta name="keywords" content="tattoo removal videos, laser tattoo removal results, before and after tattoo removal, Wrentham MA" />
      <meta property="og:title" content="Tattoo Removal Videos | Atomic Tattoo Removal" />
      <meta property="og:description" content="Watch real laser tattoo removal sessions and results at Atomic Tattoo Removal in Wrentham, MA." />
      <meta property="og:image" content="https://www.atomictattooremoval.com/assets/staff.png" />
      <meta property="og:url" content="https://www.atomictattooremoval.com/videos" />
    </Helmet>
    {/* Visually-hidden H1 for SEO/accessibility — the player itself is the visual focus. */}
    <h1
      style={{
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0,
      }}
    >
      Tattoo Removal Videos — Atomic Tattoo Removal, Wrentham, MA
    </h1>
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: isFullscreen ? '100vh' : '80vh',
        backgroundColor: '#000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Video Player */}
      <video
        ref={videoRef}
        key={currentVideoIndex}
        src={videoList[currentVideoIndex]}
        onEnded={handleVideoEnd}
        onClick={handleVideoClick}
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
      />

      {/* Initial Play Button Overlay */}
      {showPlayButton && (
        <Box
          onClick={startPlaying}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.7)',
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            <PlayArrowIcon sx={{ fontSize: '80px', color: '#000', marginLeft: '8px' }} />
          </Box>
        </Box>
      )}

      {/* Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s',
          '&:hover': {
            opacity: 1,
          },
        }}
      >
        {/* Video Counter */}
        <Typography variant="body2" sx={{ color: '#fff', minWidth: '100px' }}>
          {currentVideoIndex + 1} / {videoList.length}
        </Typography>

        {/* Playback Controls */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={previousVideo} sx={{ color: '#fff' }}>
            <SkipPreviousIcon />
          </IconButton>
          
          <IconButton onClick={togglePlayPause} sx={{ color: '#fff' }}>
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>
          
          <IconButton onClick={nextVideo} sx={{ color: '#fff' }}>
            <SkipNextIcon />
          </IconButton>
        </Box>

        {/* Fullscreen Toggle */}
        <IconButton onClick={toggleFullscreen} sx={{ color: '#fff' }}>
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Box>

      {/* Tap to show controls hint (for iPad) */}
      {!isFullscreen && !showPlayButton && !showControls && (
        <Box
          sx={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: '#fff',
            padding: '10px 20px',
            borderRadius: '4px',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="body2">
            Tap video for controls • Use fullscreen for display mode
          </Typography>
        </Box>
      )}
    </Box>
    </>
  );
};

export default Videos;
